import React from 'react'
import { ShopTheme, ThemePresetId } from '../data/shopConfig'
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean
  const n = parseInt(full || '000000', 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
export function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
function luminance(hex: string): number {
  const channel = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  const [r, g, b] = hexToRgb(hex)
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}
/**
 * Readability guard: merchants pick brand colours, we pick the text colour that
 * stays legible on top. A pale yellow button will get dark text automatically.
 */
export function readableTextOn(background: string): string {
  return contrastRatio(background, '#FFFFFF') >= 3.2 ? '#FFFFFF' : '#1B2333'
}
/** True when a colour is too light to carry white text — used for friendly warnings. */
export function isLowContrastForWhite(background: string): boolean {
  return contrastRatio(background, '#FFFFFF') < 3.2
}
export function shade(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex)
  const mix = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v + 255 * amount)))
      .toString(16)
      .padStart(2, '0')
  return `#${mix(r)}${mix(g)}${mix(b)}`
}
export const BUTTON_RADIUS: Record<ShopTheme['buttonShape'], string> = {
  rounded: '9999px',
  soft: '12px',
  square: '4px',
}
export interface ThemePreset {
  id: ThemePresetId
  name: string
  note: string
  apply: (brand: {
    primary: string
    accent: string
  }) => Partial<ShopTheme>
}
export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'auto',
    name: 'Auto Brand',
    note: 'Uses the colours from your logo',
    apply: ({ primary, accent }) => ({
      primary,
      accent,
      background: '#F4F7FC',
      backgroundStyle: 'tint',
      backdrop: 'plain',
      backgroundStrength: 45,
      buttonShape: 'soft',
    }),
  },
  {
    id: 'clean',
    name: 'Clean',
    note: 'White, simple and neutral',
    apply: ({ primary, accent }) => ({
      primary,
      accent,
      background: '#FFFFFF',
      backgroundStyle: 'clean',
      backdrop: 'dots',
      backgroundStrength: 10,
      buttonShape: 'soft',
    }),
  },
  {
    id: 'premium',
    name: 'Premium',
    note: 'Deeper tones, generous space',
    apply: ({ primary, accent }) => ({
      primary: shade(primary, -0.12),
      accent,
      background: '#F5F7FA',
      backgroundStyle: 'gradient',
      backdrop: 'rings',
      backgroundStrength: 35,
      buttonShape: 'soft',
    }),
  },
  {
    id: 'bold',
    name: 'Bold',
    note: 'Strong colour, high contrast',
    apply: ({ primary, accent }) => ({
      primary: shade(primary, -0.2),
      accent,
      background: '#EEF3FA',
      backgroundStyle: 'tint',
      backdrop: 'grid',
      backgroundStrength: 80,
      buttonShape: 'square',
    }),
  },
  {
    id: 'minimal',
    name: 'Minimal',
    note: 'Quiet, mostly monochrome',
    apply: ({ primary }) => ({
      primary,
      accent: '#E2E8F0',
      background: '#FFFFFF',
      backgroundStyle: 'clean',
      backdrop: 'plain',
      backgroundStrength: 0,
      buttonShape: 'rounded',
    }),
  },
]
/**
 * Stand-in for reading brand colours out of a logo or website. Returns the
 * palette we would propose to the merchant for one-click apply.
 */
export function proposeBrandTheme(source: 'logo' | 'website'): {
  primary: string
  accent: string
  background: string
  label: string
} {
  return {
    primary: '#4C7FC1',
    accent: '#F5CE3E',
    background: '#F4F7FC',
    label:
      source === 'logo'
        ? 'We picked these colours from your logo.'
        : 'We picked these colours from your website.',
  }
}
/**
 * The merchant's palette expressed as CSS custom properties. Set these once on
 * the storefront root and every shop component inherits them, so the public
 * page and the Settings preview are driven by the exact same markup.
 */
export function themeCssVars(theme: ShopTheme): React.CSSProperties {
  const t = resolveTheme(theme)
  const alphas: [string, number][] = [
    ['03', 0.03],
    ['04', 0.04],
    ['05', 0.05],
    ['07', 0.07],
    ['08', 0.08],
    ['10', 0.1],
    ['12', 0.12],
    ['14', 0.14],
    ['25', 0.25],
    ['26', 0.26],
    ['30', 0.3],
    ['35', 0.35],
    ['40', 0.4],
    ['50', 0.5],
  ]
  const vars: Record<string, string> = {
    '--mp': t.primary,
    '--mp-d': t.primaryHover,
    '--mp-deep': shade(theme.primary, -0.28),
    '--mp-deep-60': rgba(shade(theme.primary, -0.28), 0.6),
    '--mp-t': t.primaryText,
    '--ma': t.accent,
    '--ma-t': t.accentText,
    '--ma-30': rgba(theme.accent, 0.3),
    '--ma-70': rgba(theme.accent, 0.7),
    '--mp-shadow': rgba(theme.primary, 0.35),
    '--mp-card-shadow': rgba(shade(theme.primary, -0.4), 0.09),
    '--m-chat': t.chatButton,
    '--m-chat-t': t.chatButtonText,
    '--m-chat-accent': t.chatAccent,
    '--m-chat-surface': t.chatSurface,
    '--m-bg': t.pageBackground,
    '--m-on-bg': t.onSurface,
    '--m-on-bg-soft': t.onSurfaceSoft,
    '--m-hairline': t.surfaceHairline,
    '--m-hover': t.surfaceHover,
    '--m-r': t.radius,
  }
  alphas.forEach(([key, a]) => {
    vars[`--mp-${key}`] = rgba(theme.primary, a)
  })
  return vars as React.CSSProperties
}
/** Resolve the colours the storefront actually renders with. */
export function resolveTheme(theme: ShopTheme) {
  const chatButton =
    theme.useBrandAutomatically || !theme.chatButtonColor
      ? theme.primary
      : theme.chatButtonColor
  const chatAccent =
    theme.useBrandAutomatically || !theme.chatAccentColor
      ? theme.accent
      : theme.chatAccentColor
  const strength = theme.backgroundStrength / 100
  let pageBackground = '#FFFFFF'
  if (theme.backgroundStyle === 'clean') pageBackground = theme.background
  if (theme.backgroundStyle === 'custom') pageBackground = theme.background
  if (theme.backgroundStyle === 'tint')
    pageBackground = rgba(theme.primary, 0.03 + strength * 0.09)
  if (theme.backgroundStyle === 'gradient')
    pageBackground = `linear-gradient(180deg, ${rgba(theme.primary, 0.05 + strength * 0.1)} 0%, #FFFFFF 68%)`
  // A flat stand-in for whatever the page actually paints, so chrome sitting
  // directly on the background can pick text and marks that stay legible.
  const surface =
    theme.backgroundStyle === 'clean' || theme.backgroundStyle === 'custom'
      ? theme.background
      : '#FFFFFF'
  const darkSurface = contrastRatio(surface, '#FFFFFF') >= 3.2
  return {
    primary: theme.primary,
    primaryHover: shade(theme.primary, -0.1),
    primaryText: readableTextOn(theme.primary),
    accent: theme.accent,
    accentText: readableTextOn(theme.accent),
    chatButton,
    chatButtonText: readableTextOn(chatButton),
    chatAccent,
    chatSurface: theme.chatBackground === 'white' ? '#FFFFFF' : rgba(theme.primary, 0.04),
    pageBackground,
    surface,
    darkSurface,
    /** Text/mark colour guaranteed to read on the page background. */
    onSurface: darkSurface ? '#FFFFFF' : '#1B2333',
    onSurfaceSoft: darkSurface
      ? 'rgba(255, 255, 255, 0.72)'
      : 'rgba(27, 35, 51, 0.6)',
    /** Hairline that shows on either a light or dark background. */
    surfaceHairline: darkSurface
      ? 'rgba(255, 255, 255, 0.22)'
      : 'rgba(27, 35, 51, 0.1)',
    surfaceHover: darkSurface
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(27, 35, 51, 0.05)',
    radius: BUTTON_RADIUS[theme.buttonShape],
  }
}
